import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, MemoryRouter } from "react-router-dom";
import { FiCheck, FiShield, FiZap } from "react-icons/fi";
import PricingCard from "../../components/PricingCard/PricingCard";
import powerIcon from "../../assets/images/power.svg";
import starterIcon from "../../assets/images/star_icon.svg";
import startsignIcon from "../../assets/images/star_sign.svg";
import { apiGet, apiPost } from "../../apiService";
import "./Pricing.scss";
import Notification from "../../components/Notification/Notification";
import { getErrorMessage } from "../../utils/errorHelper";

// --- Inline SVGs ---
const Icons = {
  Zap: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
    </svg>
  ),
  Check: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ),
  Shield: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  Star: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>
  ),
  Power: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
      <line x1="12" y1="2" x2="12" y2="12"></line>
    </svg>
  ),
};

export const iconMap = {
  basic_icon: Icons.Star,
  pro_icon: Icons.Star,
};

const pageVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const PricingHeroArt = () => (
  <div className="page-pricing-hero-art" aria-hidden>
    <div className="page-pricing-hero-art-glow" />
    <svg
      className="page-pricing-hero-svg"
      viewBox="0 0 440 360"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="pr-line" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.3" />
        </linearGradient>
        <radialGradient id="pr-node" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.4" />
        </radialGradient>
        <filter id="pr-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        d="M40 180 L120 100 L200 200 L300 60 L380 200"
        stroke="url(#pr-line)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.8"
      />
      <circle
        className="page-pricing-hero-node"
        cx="200"
        cy="200"
        r="14"
        fill="url(#pr-node)"
        filter="url(#pr-glow)"
      />
      <circle
        className="page-pricing-hero-node"
        cx="300"
        cy="60"
        r="9"
        fill="url(#pr-node)"
        filter="url(#pr-glow)"
      />
      <circle
        className="page-pricing-hero-node"
        cx="120"
        cy="100"
        r="10"
        fill="url(#pr-node)"
        filter="url(#pr-glow)"
      />
      <rect
        x="80"
        y="150"
        width="32"
        height="20"
        rx="4"
        stroke="#4f46e5"
        strokeOpacity="0.25"
        fill="rgba(79,70,229,0.1)"
      />
      <rect
        x="280"
        y="120"
        width="36"
        height="24"
        rx="4"
        stroke="#0ea5e9"
        strokeOpacity="0.25"
        fill="rgba(14,165,233,0.1)"
      />
    </svg>
  </div>
);

const Pricing = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isPaying, setIsPaying] = useState(false);

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleExploreAPI = useCallback(() => {}, []);

  useEffect(() => {
    handleExploreAPI();
  }, [handleExploreAPI]);

  const handleEnterpriseClick = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      navigate("/billing/enterprise-request");
    } else {
      navigate("/contact");
    }
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchPlans = async () => {
    try {
      const res = await apiGet("catalog", "/plans");
      setPlans(res?.data?.plans || []);
    } catch (err) {
      console.error("Failed to fetch plans", err);
      setPlans([]);
    }
  };

  const isLoggedIn = () => {
    const token = localStorage.getItem("authToken");
    const user = localStorage.getItem("user");
    return !!(token && user);
  };

  // Buy-now flow is currently replaced by "Contact us" on all plan cards.
  // Kept for easy re-enable if direct purchase returns.
  // eslint-disable-next-line no-unused-vars
  const handleBuyNow = async (plan) => {
    if (isPaying) return;
    if (!isLoggedIn()) {
      localStorage.setItem("pendingPlan", JSON.stringify(plan));
      navigate("/signup");
      return;
    }
    try {
      const profileRes = await apiGet("user", "/profile");
      if (profileRes?.status !== "success") {
        throw new Error("Profile fetch failed");
      }
      const profile = profileRes.data;
      if (profile?.companyName) {
        localStorage.setItem("companyName", profile.companyName);
      }
      if (
        !profile.address ||
        !profile.state ||
        !profile.gstNumber ||
        profile.address.trim() === "" ||
        profile.gstNumber.trim() === ""
      ) {
        setNotification({
          type: "error",
          message:
            "Please update Address, State and GST Number in your profile before purchasing.",
        });
        setTimeout(() => {
          navigate("/user-profile");
        }, 2000);
        return;
      }
      openRazorpay(plan);
    } catch (error) {
      console.error("Profile validation error:", error);
      setNotification({
        type: "error",
        message: getErrorMessage(error, "Unable to validate profile."),
      });
    }
  };

  const openRazorpay = async (plan) => {
    if (isPaying) return;
    setIsPaying(true);
    const isLoaded = await loadRazorpay();
    if (!isLoaded) {
      setIsPaying(false);
      setNotification({
        type: "error",
        message: "Razorpay SDK failed to load",
      });
      return;
    }
    const user = JSON.parse(localStorage.getItem("user")) || {};
    try {
      const orderRes = await apiPost("billing", "/create-order", {
        planId: plan.id,
      });
      const backendOrderId = orderRes?.data?.orderId;
      if (!backendOrderId) {
        throw new Error(JSON.stringify({ message: "Failed to create order" }));
      }
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY || "dummy_key",
        amount: orderRes.data.amount,
        currency: "INR",
        name: user.companyName || user.name || "My Company",
        description: plan.planName,
        order_id: backendOrderId,
        handler: async function (response) {
          try {
            const verifyRes = await apiPost("billing", "/verify-payment", {
              orderId: backendOrderId,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            if (verifyRes?.data?.status === "Success") {
              setTimeout(() => {
                window.dispatchEvent(new Event("balance-updated"));
              }, 500);
              setIsPaying(false);
              navigate("/billing/payment-success", {
                replace: true,
                state: { payment: verifyRes.data },
              });
              return;
            }
            setIsPaying(false);
            navigate("/billing/payment-failed", {
              replace: true,
              state: {
                errorMessage:
                  verifyRes?.data?.message || "Payment verification failed",
              },
            });
          } catch (err) {
            setIsPaying(false);
            navigate("/billing/payment-failed", {
              replace: true,
              state: {
                errorMessage: getErrorMessage(err, "Payment failed"),
              },
            });
          }
        },
        modal: {
          ondismiss: function () {
            setIsPaying(false);
            setNotification({
              type: "info",
              message: "Payment cancelled",
            });
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (res) {
        setIsPaying(false);
        setNotification({
          type: "error",
          message:
            res?.error?.description || res?.error?.reason || "Payment failed",
        });
      });
      rzp.open();
    } catch (error) {
      setIsPaying(false);
      setNotification({
        type: "error",
        message: getErrorMessage(error, "Payment failed"),
      });
    }
  };

  return (
    <>
      <div className="page-page-root page-pricing-page">
        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
          />
        )}

        <motion.main initial="hidden" animate="show" variants={pageVariants}>
          {/* HERO SECTION */}
          <section
            className="page-pricing-hero"
            aria-labelledby="pricing-hero-title"
          >
            <div className="page-pricing-hero-bg" aria-hidden="true" />
            <div className="page-pricing-hero-grid" aria-hidden="true" />

            <div className="sec-container">
              <div className="page-pricing-hero-row">
                <div className="page-pricing-hero-inner">
                  <span className="page-eyebrow">Pricing</span>
                  <h1 className="page-title" id="pricing-hero-title">
                    <span className="page-title-accent">Pay per use.</span>{" "}
                    Credits that scale with you.
                  </h1>
                  <p className="page-lead">
                    One clear rate—no platform lock-in. Use API credits as you
                    go and top up when your workloads grow.
                  </p>
                  <div className="page-trust-list" role="list">
                    <span className="page-trust-pill" role="listitem">
                      1 credit = ₹10
                    </span>
                    <span className="page-trust-pill" role="listitem">
                      No monthly fee
                    </span>
                    <span className="page-trust-pill" role="listitem">
                      No annual contract
                    </span>
                  </div>
                </div>
                <PricingHeroArt />
              </div>
            </div>
          </section>

          {/* MAIN CONTENT */}
          <div className="page-main">
            <div className="sec-container">
              {/* Pay-per-use Bento */}
              <section
                className="page-value-section"
                aria-labelledby="value-title"
              >
                <div className="page-value-inner">
                  <div className="page-value-spotlight">
                    <div className="page-value-spotlight-glow" aria-hidden />
                    <p className="page-value-kicker">Unit price</p>
                    <div
                      className="page-value-rate"
                      aria-label="One credit equals ten Indian rupees"
                    >
                      <span className="page-value-credit">1 credit</span>
                      <span className="page-value-eq">=</span>
                      <span className="page-value-inr">₹10</span>
                    </div>
                    <p className="page-value-spotlight-line">
                      Transparent pay-as-you-go. Add credits whenever you need
                      them.
                    </p>
                    <div className="page-value-chips">
                      <span className="page-value-chip">
                        No monthly minimum
                      </span>
                      <span className="page-value-chip">
                        No annual contract
                      </span>
                    </div>
                  </div>
                  <div className="page-value-panel">
                    <div className="page-value-panel-icon" aria-hidden>
                      <Icons.Zap />
                    </div>
                    <h2 className="page-value-title" id="value-title">
                      Pay per use
                    </h2>
                    <p className="page-value-lead">
                      No monthly or yearly commitment—only pay for API credits
                      you actually use.
                    </p>
                    <ul className="page-value-list">
                      <li>
                        <span className="page-value-check">
                          <Icons.Check />
                        </span>
                        Billed in credits—clarity for finance and engineering
                      </li>
                      <li>
                        <span className="page-value-check">
                          <Icons.Check />
                        </span>
                        Top up your balance anytime as workloads change
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* GST Strip */}
              <p className="page-compliance-banner">
                <Icons.Shield />
                <span>
                  Listed pack prices are <strong>exclusive of 18% GST</strong>{" "}
                  (as applicable). You’ll see tax breakdown at checkout.
                </span>
              </p>

              {/* Pricing Cards */}
              {plans.length > 0 && (
                <div className="page-packs-shelf">
                  <header className="page-section-head">
                    <span className="page-eyebrow">Credit packs</span>
                    <h2 className="page-section-title">Choose a bundle</h2>
                    <p className="page-section-desc">
                      Pre-sized packs for common volumes—same ₹10 / credit rate,
                      bought in one go.
                    </p>
                  </header>
                  <div className="page-plans-grid">
                    {plans.map((plan) => (
                      <PricingCard
                        key={plan.id}
                        title={plan.planName}
                        price={`₹${plan.price}`}
                        credits={`${plan.credits.toLocaleString()} credits`}
                        popular={plan.isPopular}
                        buttonText="Contact us"
                        icon={iconMap[plan.iconName] || Icons.Star}
                        onClick={() => navigate("/contact")}
                        GstText="Exclusive of 18% GST"
                      />
                    ))}
                    <PricingCard
                      title="Enterprise pack"
                      price="Custom"
                      credits="Custom credits & pricing"
                      buttonText="Contact us"
                      icon={Icons.Power}
                      onClick={handleEnterpriseClick}
                    />
                  </div>
                </div>
              )}

              {/* Bottom CTA */}
              <div className="page-bottom-cta" role="group" aria-label="Help">
                <p>
                  Have questions about credits, GST, or enterprise terms? We’re
                  here to help.
                </p>
                <div className="page-cta-row">
                  <Link to="/contact" className="page-footer-link">
                    Talk to sales →
                  </Link>
                  <Link to="/" className="page-footer-link">
                    Back to home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.main>
      </div>
    </>
  );
};

export default Pricing;
